"use client";

import React, { useRef, useState } from "react";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import {
  MdArrowForward,
  MdOutlineChevronRight,
  MdOutlineChevronLeft,
} from "react-icons/md";

import styles from "./top-destinations.module.css";
import {
  makeLocalizedPublicHref,
  makePublicHref,
} from "@/lib/localizedRoutes/routeHelpers";
import { useDomainContext } from "@/global-context/domain-context";
import { useLocale } from "next-intl";

const DESTINATIONS = [
  {
    id: "us",
    name: "United States",
    properties: "10,243 properties",
    image_url: "/heros/germany_hero_2.png",
  },
  {
    id: "us2",
    name: "United States",
    properties: "10,243 properties",
    image_url: "/heros/uae_hero.png",
  },
  {
    id: "es",
    name: "Spain",
    properties: "12,046 properties",
    image_url: "/heros/romania_hero.png",
  },
  {
    id: "es2",
    name: "Spain",
    properties: "12,046 properties",
    image_url: "/heros/spain_hero.png",
  },
  {
    id: "it",
    name: "Italy",
    properties: "5,876 properties",
    image_url: "/heros/germany_hero.png",
  },
  {
    id: "it2",
    name: "Italy",
    properties: "5,876 properties",
    image_url: "/heros/germany_hero_2.png",
  },
  {
    id: "ae",
    name: "United Arab Emirates",
    properties: "15,672 properties",
    image_url: "/heros/uae_hero.png",
  },
  {
    id: "ae2",
    name: "United Arab Emirates",
    properties: "15,672 properties",
    image_url: "/heros/spain_hero.png",
  },
  {
    id: "fr",
    name: "France",
    properties: "8,312 properties",
    image_url: "/heros/germany_hero.png",
  },
  {
    id: "fr2",
    name: "France",
    properties: "8,312 properties",
    image_url: "/heros/germany_hero.png",
  },
  {
    id: "pt",
    name: "Portugal",
    properties: "6,421 properties",
    image_url: "/heros/germany_hero.png",
  },
  {
    id: "pt2",
    name: "Portugal",
    properties: "6,421 properties",
    image_url: "/heros/germany_hero_2.png",
  },
];

export default function TopDestinations() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const domain = useDomainContext();
  const locale = useLocale();
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasDragged, setHasDragged] = useState(false);

  const handleScrollLeft = () => {
    if (scrollRef.current) {
      const { clientWidth } = scrollRef.current;
      scrollRef.current.scrollBy({ left: -clientWidth, behavior: "smooth" });
    }
  };

  const handleScrollRight = () => {
    if (scrollRef.current) {
      const { clientWidth } = scrollRef.current;
      scrollRef.current.scrollBy({ left: clientWidth, behavior: "smooth" });
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setHasDragged(false);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => setIsDragging(false);

  const handleMouseUp = () => {
    setIsDragging(false);
    // Tiny timeout to let the click event register before resetting drag state
    setTimeout(() => setHasDragged(false), 50);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();

    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    scrollRef.current.scrollLeft = scrollLeft - walk;

    // Only register as a "drag" if moved more than 5px to prevent blocking clean clicks
    if (Math.abs(walk) > 5) {
      setHasDragged(true);
    }
  };

  const searchHref = makeLocalizedPublicHref(domain, locale, "locations");

  return (
    <section className={styles.section}>
      {/* Header Area */}
      <div className={styles.header}>
        <div className={styles.titleWrapper}>
          <h2 className={styles.title}>Top Real Estate Markets</h2>
          <p className={styles.subtitle}>
            Explore premium properties in the most sought-after locations.
          </p>
        </div>
        <Link href={searchHref} className={styles.viewAllLink}>
          View all markets
          <MdArrowForward className={styles.linkIcon} />
        </Link>
      </div>

      {/* Native Drag-to-Scroll Track Area */}
      <div className={styles.sliderWrapper}>
        <div
          className={`${styles.cardsTrack} ${isDragging ? styles.dragging : ""}`}
          ref={scrollRef}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
        >
          {DESTINATIONS.map((dest) => (
            <Link
              key={dest.id}
              href={`/destinations/${dest.id}`}
              className={styles.card}
              onClick={(e) => {
                if (hasDragged) e.preventDefault();
              }}
            >
              <Image
                src={dest.image_url}
                alt={dest.name}
                fill
                sizes="(max-width: 768px) 80vw, (max-width: 1200px) 40vw, 25vw"
                className={styles.cardImage}
                draggable={
                  false
                } /* CRITICAL: Prevents native ghost image drag */
              />

              <div className={styles.cardOverlay}>
                <div className={styles.cardContent}>
                  <div className={styles.textData}>
                    <h3 className={styles.cardTitle}>{dest.name}</h3>
                    <span className={styles.cardSubtitle}>
                      {dest.properties}
                    </span>
                  </div>
                  <div className={styles.iconButton}>
                    <MdOutlineChevronRight />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Carousel Controls */}
        <div className={styles.carouselControls}>
          <button
            className={styles.controlBtn}
            aria-label="Previous"
            onClick={handleScrollLeft}
          >
            <MdOutlineChevronLeft />
          </button>
          <button
            className={styles.controlBtn}
            aria-label="Next"
            onClick={handleScrollRight}
          >
            <MdOutlineChevronRight />
          </button>
        </div>
      </div>
    </section>
  );
}
